import { CSSProperties, ReactNode, useState } from "react";

import FormattedMessage from "@components/UI/FormattedMessage";
import Modal from "@components/UI/Modal";

type ButtonProps = {
  label: ReactNode;
  onClick?: () => void;
  className?: string;
};

type OnErrorClose = () => void;
type ShowErrorSecondArg = boolean | OnErrorClose;

const NOOP = () => {};

type ModalComponentProps = {
  show: boolean;
  onClose: () => void;
  message?: string;
  hideHeader?: boolean;
  customButtons?: ButtonProps | ButtonProps[];
  disableOutsideClose?: boolean;
  customContent?: ReactNode | ReactNode[];
  isCloseButtonHidden?: boolean;
  hideButtons?: boolean;
  customBodyStyles?: CSSProperties;
  title?: string;
  customTitleClass?: string;
  customClassName?: string;
};

const ModalComponent = ({
  show,
  onClose,
  message,
  hideHeader,
  customButtons,
  disableOutsideClose,
  isCloseButtonHidden = true,
  customContent,
  customBodyStyles = {},
  hideButtons = false,
  title,
  customTitleClass = "",
  customClassName = "",
}: ModalComponentProps) => {
  const ButtonTemplate = ({ onClick, label, className }: ButtonProps) => (
    <button
      type="button"
      id="modal-alert-button"
      onClick={onClick}
      className={`modal-alert-button button-secondary-color ${
        className || ""
      }`.trim()}
    >
      {label}
    </button>
  );

  const defaultButtons = [
    <ButtonTemplate key={0} onClick={onClose} label="OK" />,
  ];

  const buttons = customButtons
    ? Array.isArray(customButtons)
      ? customButtons.map((button, index) => (
          <ButtonTemplate key={index} {...button} />
        ))
      : [<ButtonTemplate key={0} {...customButtons} />]
    : defaultButtons;
  const isSingleButton = buttons.length === 1;

  return (
    <Modal
      isActive={show}
      onClose={onClose}
      isCloseButtonHidden={isCloseButtonHidden}
      disableOutsideClickClose={!disableOutsideClose}
    >
      <div
        style={customBodyStyles}
        className={`modal-alert ${customClassName}`.trim()}
      >
        <div className="modal-alert-label">
          {!hideHeader && (
            <div className={customTitleClass}>
              {title || <FormattedMessage id="Alert.label.error" />}
            </div>
          )}
        </div>
        <div className="modal-alert-content">
          {customContent || message || "Undefined content"}
        </div>
        {hideButtons === false && (
          <div
            className={`modal-alert-buttons${
              isSingleButton ? " modal-alert-buttons--single" : ""
            }`}
          >
            {buttons}
          </div>
        )}
      </div>
    </Modal>
  );
};

type Props = {
  onClose?: () => void;
  hideHeader?: boolean;
  customTitle?: string;
  customClassName?: string;
  customButtons?: ButtonProps | ButtonProps[];
  disableOutsideClose?: boolean;
};

const useModalAlert = (props?: Props) => {
  const [show, setShow] = useState<boolean>(false);
  const [isCallbackDisabled, setIsCallbackDisabled] = useState<boolean>(false);
  const [message, setMessage] = useState<string>(null);
  const [onClose, setOnClose] = useState<OnErrorClose>(() => NOOP);

  const resolveShowErrorOptions = (
    disableCallbackOrOnClose: ShowErrorSecondArg,
    onErrorClose?: OnErrorClose
  ) => {
    if (typeof disableCallbackOrOnClose === "function") {
      return {
        disableCallback: false,
        onErrorClose: disableCallbackOrOnClose,
      };
    }

    return {
      disableCallback: disableCallbackOrOnClose ?? false,
      onErrorClose,
    };
  };

  const showError = (
    errorMessage: string,
    disableCallbackOrOnClose: ShowErrorSecondArg = false,
    onErrorClose?: OnErrorClose
  ) => {
    const {
      disableCallback,
      onErrorClose: resolvedOnErrorClose,
    } = resolveShowErrorOptions(disableCallbackOrOnClose, onErrorClose);

    setMessage(errorMessage);
    setShow(true);
    setIsCallbackDisabled(disableCallback);
    setOnClose(() => resolvedOnErrorClose || NOOP);
  };

  const onModalClose = () => {
    setShow(false);
    setMessage(null);
    if (props?.onClose && !isCallbackDisabled) {
      props?.onClose();
    }
    onClose();
    setOnClose(() => NOOP);
    setIsCallbackDisabled(false);
  };

  const Component = () => (
    <ModalComponent
      show={show}
      onClose={onModalClose}
      message={message}
      hideHeader={props?.hideHeader}
      title={props?.customTitle}
      customClassName={props?.customClassName}
      customButtons={props?.customButtons}
      disableOutsideClose={props?.disableOutsideClose}
    />
  );

  return [Component, showError];
};

type LegacyModalAlertProps = {
  show: boolean;
  onClose: () => void;

  message?: string;
  hideHeader?: boolean;
  customButtons?: ButtonProps | ButtonProps[];
  disableOutsideClose?: boolean;
  hideButtons?: boolean;
  isCloseButtonHidden?: boolean;
  customContent?: ReactNode | ReactNode[];
  customBodyStyles?: CSSProperties;
  customTitleClass?: string;
  title?: string;
  customClassName?: string;
};

export const LegacyModalAlert = ({
  show,
  onClose,
  message,
  hideHeader,
  customButtons,
  disableOutsideClose,
  customContent,
  customBodyStyles,
  isCloseButtonHidden,
  hideButtons,
  customTitleClass,
  title,
  customClassName,
}: LegacyModalAlertProps) => {
  return (
    <ModalComponent
      show={show}
      onClose={onClose}
      title={title}
      customTitleClass={customTitleClass}
      isCloseButtonHidden={isCloseButtonHidden}
      hideButtons={hideButtons}
      message={message}
      hideHeader={hideHeader}
      customButtons={customButtons}
      disableOutsideClose={disableOutsideClose}
      customContent={customContent}
      customBodyStyles={customBodyStyles}
      customClassName={customClassName}
    />
  );
};

export default useModalAlert;
